import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SharedJudgmentData {
  judgment: {
    id: number;
    caseNumbers: string[];
    courtType: string;
    judgmentDate: string | null;
    judgmentType: string;
    summary: string | null;
    textContent: string;
    judges: string[];
    keywords: string[];
    legalBases: string[];
  };
  notes: string | null;
  sharedBy: string;
  sharedAt: string;
  viewCount: number;
}

export default function SharedJudgmentPage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedJudgmentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedJudgment = async () => {
      if (!token) {
        setError(t('shared.noToken'));
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/v1/public/judgment/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(t('shared.judgmentNotFound'));
          }
          throw new Error(t('shared.loadJudgmentError'));
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedJudgment();
  }, [token]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return t('shared.noDate');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="text-slate-600 dark:text-slate-400">{t('shared.loadingJudgment')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            {t('shared.notFound')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            {t('shared.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { judgment, notes, viewCount } = data;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">{t('brand.name')}</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('shared.sharedJudgment')}</p>
              </div>
            </Link>
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span>{t('shared.views', { count: viewCount })}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          {/* Judgment header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                {t('courtType.' + judgment.courtType)}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {formatDate(judgment.judgmentDate)}
              </span>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              {judgment.caseNumbers?.join(', ') || t('shared.judgmentId', { id: judgment.id })}
            </h2>
          </div>

          <div className="px-6 py-4 space-y-6">
            {/* Shared notes */}
            {notes && (
              <section className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-2">
                  {t('shared.noteFromSharer')}
                </h3>
                <p className="text-slate-700 dark:text-slate-300">{notes}</p>
              </section>
            )}

            {/* Summary */}
            {judgment.summary && (
              <section>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  {t('shared.summary')}
                </h3>
                <p className="text-slate-700 dark:text-slate-300">{judgment.summary}</p>
              </section>
            )}

            {/* Legal Bases */}
            {judgment.legalBases && judgment.legalBases.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  {t('shared.legalBases')}
                </h3>
                <ul className="list-disc list-inside space-y-1">
                  {judgment.legalBases.map((base, index) => (
                    <li key={index} className="text-slate-700 dark:text-slate-300 text-sm">
                      {typeof base === 'string' ? base : JSON.stringify(base)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Judges */}
            {judgment.judges && judgment.judges.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  {t('shared.judges')}
                </h3>
                <ul className="space-y-1">
                  {judgment.judges.map((judge, index) => (
                    <li key={index} className="text-slate-700 dark:text-slate-300">
                      {judge}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Keywords */}
            {judgment.keywords && judgment.keywords.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  {t('shared.keywords')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {judgment.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded"
                    >
                      {typeof keyword === 'string' ? keyword : JSON.stringify(keyword)}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Full text */}
            {judgment.textContent && (
              <section>
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  {t('shared.content')}
                </h3>
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <div
                    className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: judgment.textContent }}
                  />
                </div>
              </section>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {t('shared.searchMore')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {t('shared.goToSearch')}
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            {t('footer.text')}
          </p>
        </div>
      </footer>
    </div>
  );
}
